import React, { useState } from "react";
import { Button } from "@/src/components/ui/button";
import MainHeroContent from "./main";
import Nav from "./nav";
import VuesaxBoldMessages from "@/src/components/icons/Vuesax/vuesaxBoldMessages";
import { Send, X, Mail } from "lucide-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { sendHomeEmail } from "@/src/redux/users/features";
import { useAppDispatch } from "@/src/redux/store";
import { useToastListener } from "@/src/hooks/useToastListner";

type HeroProps = {
  howItWorksRef: React.RefObject<HTMLElement>;
};

// ✅ Validation Schema
const ContactSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  message: Yup.string().min(5, "Message must be at least 5 characters").required("Message is required"),
});

const Hero: React.FC<HeroProps> = ({ howItWorksRef }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch()
  useToastListener()
  const SendMessage = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm"
        onClick={() => setIsModalOpen(false)}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white bg-opacity-10 backdrop-blur-xl border border-white border-opacity-20 rounded-2xl shadow-2xl w-full max-w-md p-8 animate-in zoom-in duration-300">
        {/* Close Button */}
        <button
          onClick={() => setIsModalOpen(false)}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4 mx-auto">
            <Mail className="text-white" size={32} />
          </div>
          <h2 className="text-3xl font-bold text-white text-center mb-2">Get In Touch</h2>
          <p className="text-white text-opacity-80 text-center">
            Send us a message and we'll get back to you soon
          </p>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ email: "", message: "" }}
          validationSchema={ContactSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            // simulate API request
            await dispatch(sendHomeEmail(values.email, values.message))
            setSubmitting(false);
            resetForm();
            setIsModalOpen(false);
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                  Email Address
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all"
                />
                {errors.email && touched.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                  Message
                </label>
                <Field
                  as="textarea"
                  id="message"
                  name="message"
                  placeholder="Tell us what's on your mind..."
                  rows={5}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all resize-none"
                />
                {errors.message && touched.message && (
                  <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-6 bg-white text-black font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </Form>
          )}
        </Formik>

        {/* Additional Info */}
        <div className="mt-6 pt-6 border-t border-white border-opacity-20">
          <p className="text-white text-opacity-70 text-sm text-center">
            Or email us directly at{" "}
            <a
              href="mailto:shortlet@myblankspace.com"
              className="text-white font-semibold hover:underline"
            >
              shortlet@myblankspace.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section className="min-h-[800px] h-screen relative w-full">
      <div className="absolute w-full h-full left-0 top-0 z-0">
        <div className="bg-black bg-opacity-60 absolute w-full h-full"></div>
        <img
          className="w-full h-full object-cover"
          src="https://storage.googleapis.com/myblankspace-prod/assets/bg.png"
          alt="Hero background"
        />
      </div>
      <div className="z-10 relative">
        <Nav howItWorksRef={howItWorksRef} />
        <MainHeroContent />
      </div>
      <Button
        className="h-[60px] w-[60px] md:h-[70px] md:w-[70px] bg-black bg-opacity-10 backdrop-blur-lg border border-white border-opacity-10 mt-5 rounded-full absolute right-5 bottom-10 md:right-12.5 md:bottom-25"
        onClick={() => setIsModalOpen(true)}
        aria-label="Open contact modal"
      >
        <VuesaxBoldMessages className="fill-white" />
      </Button>
      <div className="w-full absolute bottom-0 left-0 flex justify-between">
        <div className="w-12.5 h-6.25 bg-white"></div>
        <div className="w-12.5 h-6.25 bg-white"></div>
      </div>
      {/* Modal */}
      {isModalOpen && <SendMessage />}
    </section>
  );
};

export default Hero;

